/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: NotificationContext.tsx
 🗓️ Created: 2025-01-18
 🧩 Description:
    Global notification context provider using Sonner for elegant
    toast notifications across the entire application.

 🔧 Usage:
    <NotificationProvider>
      <App />
    </NotificationProvider>

 🔁 Last Updated: 2026-01-22 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';

// Keep interface for backward compatibility
export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
}

interface NotificationContextType {
  notifications: NotificationData[];
  addNotification: (notification: Omit<NotificationData, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  // Empty array for backward compatibility - Sonner manages state internally
  const notifications: NotificationData[] = [];

  const addNotification = useCallback((notification: Omit<NotificationData, 'id'>) => {
    const { type, message, duration } = notification;

    switch (type) {
      case 'success':
        toast.success(message, { duration: duration || 3000 });
        break;
      case 'error':
        toast.error(message, { duration: duration || 5000 });
        break;
      case 'warning':
        toast.warning(message, { duration: duration || 4000 });
        break;
      case 'info':
        toast.info(message, { duration: duration || 3000 });
        break;
      default:
        toast(message, { duration: duration || 3000 });
    }
  }, []);

  const removeNotification = useCallback((_id: string) => {
    // Sonner manages dismissal automatically
  }, []);

  const clearAll = useCallback(() => {
    toast.dismiss();
  }, []);

  const showSuccess = useCallback((message: string, duration = 3000) => {
    toast.success(message, { duration });
  }, []);

  const showError = useCallback((message: string, duration = 5000) => {
    toast.error(message, { duration });
  }, []);

  const showWarning = useCallback((message: string, duration = 4000) => {
    toast.warning(message, { duration });
  }, []);

  const showInfo = useCallback((message: string, duration = 3000) => {
    toast.info(message, { duration });
  }, []);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  // No need to render NotificationContainerSimple - Sonner's Toaster in main.tsx handles display
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
}

// Re-export for backward compatibility
export { useNotificationContext as useNotifications };
